/**
 * TODO - preamble
 */
import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    AddressComponent,
    CityComponent,
    CompanyComponent,
    EmailComponent as Email,
    PhoneNumberComponent as PhoneNumber,
    EmploymentPersonalInfoResponse,
    EmploymentPersonalInfoViewModel,
    ExtendedFieldValidationErrors,
    FieldValidationErrors,
    FirstNameComponent as FirstName,
    FormFooterComponent as FormFooter,
    getCompanyName,
    handleEmailOnKeyDown,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    isFormFilled,
    LastNameComponent as LastName,
    LogoComponent,
    MarketConsentWrapper,
    Metadata,
    Organization,
    PostalCodeComponent,
    setRef,
    StateComponent,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    VerificationService,
    VerificationStepsEnum,
} from '../../es6';

interface StepEmploymentPersonalInfoComponentProps {
    verificationService: VerificationService;
}

const StepEmploymentPersonalInfo = ({ verificationService }: StepEmploymentPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as EmploymentPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as EmploymentPersonalInfoResponse;

    const updateEmploymentViewModel = (key: keyof EmploymentPersonalInfoViewModel, value: any) => {
        const nextState: EmploymentPersonalInfoViewModel = produce(viewModel, (draft: EmploymentPersonalInfoViewModel) => {
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    return (
        <div id="sid-step-employment-personal-info" className="sid-l-container">
            <div className="sid-header">
                <div className="sid-l-horz-center">
                    <LogoComponent verificationService={verificationService} />
                </div>
                <div className="sid-header__title sid-l-horz-center">
                    <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Employment-Only Offer" />
                </div>

                <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a current employee at an eligible company." />
                    &nbsp;
                    <HowDoesVerifyingWork verificationService={verificationService} />
                </div>
            </div>

            <CompanyComponent
                value={(verificationService.viewModel as EmploymentPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue) => {
                            updateEmploymentViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue) => {
                            updateEmploymentViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <AddressComponent
                isErrored={!!fieldValidationErrors.address1}
                errorId={fieldValidationErrors.address1}
                onChange={(newValue) => {
                    updateEmploymentViewModel('address1', newValue);
                    updateFieldValidationErrorsByFieldId('address1', newValue, verificationService);
                }}
                value={viewModel.address1}
            />

            <div className="sid-thirds">
                <CityComponent
                    isErrored={!!fieldValidationErrors.city}
                    errorId={fieldValidationErrors.city}
                    onChange={(newValue) => {
                        updateEmploymentViewModel('city', newValue);
                        updateFieldValidationErrorsByFieldId('city', newValue, verificationService);
                    }}
                    value={viewModel.city}
                />

                <StateComponent
                    isErrored={!!fieldValidationErrors.state}
                    errorId={fieldValidationErrors.state}
                    onChange={(newValue) => {
                        updateEmploymentViewModel('state', newValue);
                        updateFieldValidationErrorsByFieldId('state', newValue, verificationService);
                    }}
                    value={viewModel.state}
                />

                <PostalCodeComponent
                    isErrored={!!fieldValidationErrors.postalCode}
                    onChange={(newValue) => {
                        updateEmploymentViewModel('postalCode', newValue);
                        updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                    }}
                    value={viewModel.postalCode}
                />
            </div>

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Needed to send you your unique code"
                    />
                }
                onChange={
                    (newValue) => {
                        updateEmploymentViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={event => handleEmailOnKeyDown(event)}
            />
            {
                verificationService.programTheme.isSmsNotifierConfigured ?
                    <PhoneNumber
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={
                            (newValue) => {
                                updateEmploymentViewModel('phoneNumber', newValue);
                                updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                            }
                        }
                    />
                    : null
            }
            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateEmploymentViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-wrapper__collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectEmployeePersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={button => setRef('submitButton', button)}
                    >
                        <FormattedHTMLMessage id="verifyMyEmploymentStatus" defaultMessage="Verify My Employment Status" />
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="employmentInfoShared"
                    values={{
                        companyName: getCompanyName(verificationService.programTheme),
                    }}
                    defaultMessage={'Information entered here will be used for verification purposes. '}
                />

            </FormFooter>
        </div>
    );
};

export const StepEmploymentPersonalInfoComponent = injectIntl(StepEmploymentPersonalInfo);
